/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { Person } from '../person'
import { SectionTitle } from '../sectionTitle'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { nest } from 'd3-collection'

// TODO Set the correct article ID here
const ARTICLE_ID = '766870256747'

function resolveMediaName() {
  const host = window.location.hostname
  const isLocalDev =
    /^(localhost|127\.0\.0\.1)$/.test(host) ||
    /^\d+\.\d+\.\d+\.\d+$/.test(host)

  return parseHostname(
    isLocalDev ? 'interaktiv.tagesanzeiger.ch' : undefined
  ).publicationName
}

const mediaName = resolveMediaName() // "tagesanzeiger", "bazonline", "tdg", etc.
console.log('mediaName:', mediaName)

export class AppComponent extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      tenantText: {},
      peopleData: {},
      output: null
    }
  }

  extractPublicationDate(rawData) {
    // extract publication date
    const months =
      mediaName === 'tdg' || mediaName === '24heures'
        ? [
            'Janvier',
            'Février',
            'Mars',
            'Avril',
            'Mai',
            'Juin',
            'Juillet',
            'Août',
            'Septembre',
            'Octobre',
            'Novembre',
            'Décembre'
          ]
        : [
            'Januar',
            'Februar',
            'März',
            'April',
            'Mai',
            'Juni',
            'Juli',
            'August',
            'September',
            'Oktober',
            'November',
            'Dezember'
          ]
    const dateObject = new Date(rawData.updated)
    const d = dateObject.getDate()
    const m = dateObject.getMonth()
    const y = dateObject.getFullYear()
    return d + '. ' + months[m] + ' ' + y
  }

  componentDidMount() {
    // console.log('componentDidMount')
    /* GET SHEET DATA VIA CLOUD STORAGE */
    const textURL =
      'https://storage.googleapis.com/ta-interaktiv/mitarbeiter-der-redaktion/2022-texte.json'
    // console.log('dataURL:', dataURL)
    fetch(textURL)
      .then(response => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error('JSON Data was not OK')
        }
      })
      .then(responseData => {
        // console.log('responseData:', responseData)
        const tenantText = responseData.filter(function(el) {
          return (
            el.tenant ===
            (mediaName === 'derbund' ? 'bernerzeitung' : mediaName)
          )
        })
        // console.log('tenantText:', tenantText)
        this.setState({ tenantText: tenantText[0] || {} })
      })
      .catch(error => console.error('Failed to load tenant text:', error))

    /* GET SHEET DATA VIA CLOUD STORAGE */
    const dataURL =
      'https://storage.googleapis.com/ta-interaktiv/mitarbeiter-der-redaktion/2022-' +
      (mediaName === 'derbund' ? 'bernerzeitung' : mediaName) +
      '.json'
    // console.log('dataURL:', dataURL)
    fetch(dataURL)
      .then(response => {
        if (response.ok) {
          return response.json()
        } else {
          throw new Error('JSON Data was not OK')
        }
      })
      .then(responseData => {
        // console.log('responseData:', responseData)
        /* GROUP DATA BY REDAKTION */
        const groupedData = nest()
          .key(function(d) {
            return d.ressort
          })
          .entries(responseData.data)
        // console.log('groupedData:', groupedData)

        this.setState(
          {
            peopleData: responseData,
            publicationDate: this.extractPublicationDate(responseData),
            groupedData: groupedData
          },
          function() {
            const output = this.state.groupedData.map(function(bereich, index) {
              return (
                <div key={index} className='ui vertical segment'>
                  <div className='ui container'>
                    <SectionTitle mediaName={mediaName}>
                      {bereich.key}
                    </SectionTitle>
                    <div className='ui centered grid'>
                      {(() => {
                        // Process persons: filter out empty ones and track when to start new rows
                        const processedPersons = []
                        let shouldStartNewRow = false

                        bereich.values.forEach((person, i) => {
                          const isEmpty =
                            (!person.unityID || person.unityID.trim() === '') &&
                            (!person.names || person.names.trim() === '') &&
                            (!person.title || person.title.trim() === '')

                          if (isEmpty) {
                            // Skip empty person, mark that next person should start on new row
                            shouldStartNewRow = true
                          } else {
                            // Add person with row break flag if previous was empty
                            processedPersons.push({
                              person: person,
                              startNewRow: shouldStartNewRow
                            })
                            shouldStartNewRow = false
                          }
                        })

                        return processedPersons.map((item, i) => (
                          <React.Fragment key={i}>
                            {item.startNewRow && (
                              <div style={{ clear: 'both', width: '100%' }} />
                            )}
                            <Person
                              unityID={item.person.unityID}
                              name={item.person.names}
                              customImage={item.person.customImage}
                              unityImage={item.person.unityImage}
                              function={item.person.title}
                              hasBiography={item.person.hasBiography}
                              hasArticles={item.person.hasArticles}
                              mediaName={mediaName}
                            />
                          </React.Fragment>
                        ))
                      })()}
                    </div>
                  </div>

                  {index === 0 && (
                    <div className='ui text container'>
                      <div className='ui icon hover-hint message'>
                        <i className='hand pointer outline icon' />
                        <div className='content'>
                          <p
                            dangerouslySetInnerHTML={{
                              __html:
                                mediaName === 'tdg' || mediaName === '24heures'
                                  ? 'Cliquez sur le portrait pour en savoir plus sur la personne.'
                                  : 'Tippen Sie auf die Fotos, um zusätzliche Informationen zu den Redaktorinnen und Redaktoren zu erhalten.'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })
            this.setState({ output: output })
          }
        )
      })
      .catch(error => console.error('Failed to load people data:', error))
  }

  render() {
    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} />

        <ThemeSwitch />

        <header className='ui vertical  very fitted  segment'>
          <div className='ui container'>
            <div className='ui header'>
              <h1
                dangerouslySetInnerHTML={{
                  __html: this.state.tenantText?.title ?? ''
                }}
              />
              <h2
                dangerouslySetInnerHTML={{
                  __html: this.state.tenantText?.lead ?? ''
                }}
              />
            </div>
          </div>
        </header>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta updated'>
                {mediaName === 'tdg' || mediaName === '24heures'
                  ? 'Mis à jour le '
                  : 'Aktualisiert am '}{' '}
                {this.state.publicationDate}
              </div>
            </div>
            <div
              className='ta text'
              dangerouslySetInnerHTML={{
                __html: this.state.tenantText?.introduction ?? ''
              }}
            />
          </div>
        </div>

        {this.state.output}

        <Footer
          inverted
          articleId={ARTICLE_ID}
          hidePortfolioTeaser
          hideTop
          credits={
            mediaName === 'tdg' || mediaName === '24heures'
              ? [
                  ['Mise en œuvre', 'Mathias Lutz'],
                  ['Gestion de projet', 'Olivier Cretton']
                ]
              : [
                  ['Umsetzung', 'Mathias Lutz'],
                  ['Projektleitung', 'Claudia Bianchi']
                ]
          }
        />
      </div>
    )
  }
}
